import { useMemo, useState } from 'react'
import { COVERAGE_REPORT_STATUS_LABEL, toReportRows, type CoverageReportStatus } from './coverageReport'
import ExportBar from './ExportBar'
import {
  LEAVE_DECISION_LABEL,
  LEAVE_STATE_LABEL,
  buildLeaveRows,
  filterLeaveRows,
  totalLeaveDays,
  type LeaveDecision,
  type LeaveFilters,
  type LeaveRequestState,
  type LeaveRow,
  type LeaveSource,
} from './leaveFigures'
import { leaveExportTable } from './leaveReportExport'
import { getRequestedLeave, getScheduledLeave } from './leaveReportStore'
import ReportControls, { type ReportFilter } from './ReportControls'
import ReportTable, { type ReportTableColumn } from './ReportTable'
import { fullDate } from './reportDates'
import { generatedLabel } from './reportExport'
import { nextSort, sortRows, type ReportSort, type SortValue } from './reportSort'
import { supervisorLabel } from './supervisorLabel'
import { useCoverageData } from './useCoverageData'
import { useLoaded } from './useLoaded'
import { useReportRange } from './useReportRange'

/**
 * docs/product/reports.md's leave and availability report: leave as requested
 * (the requests and their decisions) or as scheduled (the published
 * schedule), with its effect on the team's coverage on those dates.
 */
const COVERAGE_CLASS: Partial<Record<CoverageReportStatus, string>> = {
  understaffed: 'text-error',
  'missing-qualification': 'text-error',
  'at-risk': 'text-warning',
}

function dateRange(row: LeaveRow): string {
  return row.startDate === row.endDate ? fullDate(row.startDate) : `${fullDate(row.startDate)} to ${fullDate(row.endDate)}`
}

const BASE_COLUMNS: ReportTableColumn<LeaveRow>[] = [
  { id: 'employee', label: 'Employee', cell: (row) => row.employee },
  { id: 'team', label: 'Team', cell: (row) => row.team },
  { id: 'startDate', label: 'Dates', cell: dateRange },
  { id: 'days', label: 'Days', numeric: true, cell: (row) => row.days },
  { id: 'leaveType', label: 'Leave type', cell: (row) => row.leaveType },
]

const REQUEST_COLUMNS: ReportTableColumn<LeaveRow>[] = [
  { id: 'state', label: 'Request state', cell: (row) => (row.state ? LEAVE_STATE_LABEL[row.state] : '') },
  { id: 'decision', label: 'Decision', cell: (row) => (row.decision ? LEAVE_DECISION_LABEL[row.decision] : 'None') },
  { id: 'decidedBy', label: 'Decided by', cell: (row) => row.decidedBy ?? '' },
  { id: 'decidedAt', label: 'Decided at', cell: (row) => (row.decidedAt ? generatedLabel(new Date(row.decidedAt)) : '') },
]

const EFFECT_COLUMN: ReportTableColumn<LeaveRow> = {
  id: 'coverageEffect',
  label: 'Coverage effect',
  cell: (row) => <span className={`font-semibold ${COVERAGE_CLASS[row.coverage] ?? ''}`}>{row.coverageEffect}</span>,
}

const SORT_VALUE: Record<string, (row: LeaveRow) => SortValue> = {
  employee: (row) => row.employee,
  team: (row) => row.team,
  startDate: (row) => row.startDate,
  days: (row) => row.days,
  leaveType: (row) => row.leaveType,
  state: (row) => row.state,
  decision: (row) => row.decision,
  decidedBy: (row) => row.decidedBy,
  decidedAt: (row) => row.decidedAt,
  coverageEffect: (row) => row.coverageEffect,
}

function Tile({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-card border border-line bg-canvas p-4">
      <p className="text-caption font-bold tracking-wide text-ink-subtle uppercase">{label}</p>
      <p className="mt-1 text-h2 font-extrabold [font-variant-numeric:tabular-nums]">{value}</p>
    </div>
  )
}

export default function LeaveReport() {
  const range = useReportRange()
  const { from, to } = range
  const [source, setSource] = useState<LeaveSource>('requested')
  const [filters, setFilters] = useState<LeaveFilters>({})
  const [sort, setSort] = useState<ReportSort>({ column: 'startDate', direction: 'asc' })
  const coverage = useCoverageData(from, to)
  const leave = useLoaded(
    () => (range.invalid ? Promise.resolve([]) : source === 'requested' ? getRequestedLeave(from, to) : getScheduledLeave(from, to)),
    [],
    [source, from, to],
  )
  const { lookups } = coverage

  const coverageRows = useMemo(() => toReportRows(coverage.rows, lookups), [coverage.rows, lookups])
  const allRows = useMemo(
    () => buildLeaveRows(leave.data, { from, to }, coverageRows, lookups.supervisors),
    [leave.data, from, to, coverageRows, lookups],
  )
  const filtered = useMemo(() => filterLeaveRows(allRows, filters), [allRows, filters])
  const columns = source === 'requested' ? [...BASE_COLUMNS, ...REQUEST_COLUMNS, EFFECT_COLUMN] : [...BASE_COLUMNS, EFFECT_COLUMN]
  const sortColumn = columns.some((column) => column.id === sort.column) ? sort.column : 'startDate'
  const rows = useMemo(() => sortRows(filtered, SORT_VALUE[sortColumn], sort.direction), [filtered, sortColumn, sort.direction])
  const days = totalLeaveDays(filtered)
  const loading = leave.loading || coverage.loading
  const failed = leave.failed || coverage.failed

  function chooseSource(next: LeaveSource) {
    setSource(next)
    // A scheduled entry has no request behind it, so the request filters no
    // longer apply.
    setFilters((current) => ({ ...current, state: undefined, decision: undefined }))
  }

  const set = (key: keyof LeaveFilters) => (value: string) => setFilters((current) => ({ ...current, [key]: value || undefined }))
  const reportFilters: ReportFilter[] = [
    {
      id: 'team',
      label: 'Team',
      allLabel: 'All teams',
      value: filters.teamId ?? '',
      options: Array.from(new Set(allRows.map((row) => row.teamId))).map((id) => ({ value: id, label: lookups.teams.get(id) ?? 'Unknown team' })),
      onChange: set('teamId'),
    },
    {
      id: 'supervisor',
      label: 'Supervisor',
      allLabel: 'All supervisors',
      value: filters.supervisorId ?? '',
      options: Array.from(new Set(allRows.flatMap((row) => (row.supervisorId ? [row.supervisorId] : [])))).map((id) => ({
        value: id,
        label: supervisorLabel(id, lookups),
      })),
      onChange: set('supervisorId'),
    },
    ...(source === 'requested'
      ? [
          {
            id: 'state',
            label: 'Request state',
            allLabel: 'All states',
            value: filters.state ?? '',
            options: (Object.keys(LEAVE_STATE_LABEL) as LeaveRequestState[]).map((state) => ({ value: state, label: LEAVE_STATE_LABEL[state] })),
            onChange: set('state'),
          },
          {
            id: 'decision',
            label: 'Decision',
            allLabel: 'All decisions',
            value: filters.decision ?? '',
            options: (Object.keys(LEAVE_DECISION_LABEL) as LeaveDecision[]).map((decision) => ({ value: decision, label: LEAVE_DECISION_LABEL[decision] })),
            onChange: set('decision'),
          },
        ]
      : []),
    {
      id: 'leaveType',
      label: 'Leave type',
      allLabel: 'All leave types',
      value: filters.leaveType ?? '',
      options: Array.from(new Set(allRows.map((row) => row.leaveType))).sort().map((name) => ({ value: name, label: name })),
      onChange: set('leaveType'),
    },
    {
      id: 'coverage',
      label: 'Coverage effect',
      allLabel: 'All coverage effects',
      value: filters.coverage ?? '',
      options: (Object.keys(COVERAGE_REPORT_STATUS_LABEL) as CoverageReportStatus[]).map((status) => ({
        value: status,
        label: COVERAGE_REPORT_STATUS_LABEL[status],
      })),
      onChange: set('coverage'),
    },
  ]

  return (
    <div className="flex flex-col gap-5">
      <section aria-label="Totals" className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Tile label={source === 'requested' ? 'Requests' : 'Leave periods'} value={filtered.length} />
        <Tile label="Leave days" value={days} />
        {source === 'requested' && <Tile label="Approved" value={filtered.filter((row) => row.state === 'approved').length} />}
        {source === 'requested' && <Tile label="Awaiting decision" value={filtered.filter((row) => row.state === 'pending').length} />}
        <Tile
          label="Coverage conflicts"
          value={filtered.filter((row) => row.coverage === 'understaffed' || row.coverage === 'missing-qualification').length}
        />
      </section>

      <div role="group" aria-label="Leave source" className="flex">
        {(['requested', 'scheduled'] as const).map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={source === option}
            onClick={() => chooseSource(option)}
            className={`min-h-11 border border-line-strong px-4 text-small font-bold first:rounded-l-control last:rounded-r-control ${
              source === option ? 'border-primary bg-selected' : 'hover:bg-surface-2'
            }`}
          >
            {option === 'requested' ? 'Requested leave' : 'Scheduled leave'}
          </button>
        ))}
      </div>

      <ReportControls
        range={range}
        filters={reportFilters}
        search={{ value: filters.search ?? '', onChange: set('search') }}
        onReset={() => setFilters({})}
      />

      <ExportBar
        count={`${rows.length} ${rows.length === 1 ? 'row' : 'rows'}`}
        loading={loading}
        disabled={loading || failed || range.invalid || rows.length === 0}
        fileStem={`muster-leave-${source}-${from}-to-${to}`}
        table={() => leaveExportTable(rows, days, { source, from, to, filters, lookups, generatedAt: new Date() })}
      />

      {failed ? (
        <p role="alert" className="rounded-card border border-line bg-canvas p-4 text-small text-error">
          The leave report could not be loaded. Reload the page, or try again in a moment.
        </p>
      ) : (
        <ReportTable
          label="Leave and availability"
          caption={source === 'requested' ? 'Leave requests and their decisions' : 'Leave in the published schedule'}
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          sort={{ ...sort, column: sortColumn }}
          onSort={(column) => setSort((current) => nextSort(current, column))}
          loading={loading}
          empty={
            source === 'requested'
              ? 'No leave requests in these dates. Choose another pay period or clear a filter.'
              : 'No leave in a published schedule for these dates. Choose another pay period or clear a filter.'
          }
          minWidth={1200}
        />
      )}
    </div>
  )
}
